import * as React from 'react'

import { observer } from 'mobx-react'

import MarkupContextToolbar from './MarkupContextToolbar'
import MarkupEditorStore, { TOOLBAR_GAP } from './MarkupEditorStore'
import MarkupSelectionChrome from './MarkupSelectionChrome'
import MarkupSubToolbar from './MarkupSubToolbar'
import MarkupTextContextToolbar from './MarkupTextContextToolbar'
import MarkupToolbar from './MarkupToolbar'

interface IProps {
  store: MarkupEditorStore
  // Shell-prop parity only; mobile has no browser-fullscreen, so it's unused.
  portalContainer?: HTMLElement | null
}

const MobileMarkupEditor: React.FC<IProps> = observer(({ store }) => (
  <div
    ref={store.overlayRef}
    className="tw:pointer-events-none tw:absolute tw:inset-0 tw:z-10"
  >
    <MarkupSelectionChrome store={store} />
    {/* Hidden during precise-location edit — the move-mode action bar owns the
          screen then (matches DesktopMarkupEditor). */}
    {!store.isLocationEditActive && (
      <div className="tw:pointer-events-auto tw:absolute tw:bottom-[var(--markup-toolbar-bottom,0.75rem)] tw:left-1/2 tw:flex tw:-translate-x-1/2 tw:flex-col tw:items-center tw:gap-2">
        <MarkupSubToolbar
          isMobileEnv={true}
          stylePreview={store.stylePreview}
          activeTool={store.activeTool}
          color={store.color}
          strokeWidth={store.strokeWidth}
          shapeKind={store.shapeKind}
          lineType={store.lineType}
          drawType={store.drawType}
          fillColor={store.fillColor}
          fillStyle={store.fillStyle}
          onColorChange={store.changeColor}
          onStrokeWidthChange={store.changeStrokeWidth}
          onShapeKindChange={store.changeShapeKind}
          onLineTypeChange={store.changeLineType}
          onDrawTypeChange={store.changeDrawType}
          onResetToSelect={() => store.selectTool('select')}
          onFillColorChange={store.changeFillColor}
          onFillStyleChange={store.changeFillStyle}
          polygonDrawing={store.polygonDrawing}
          onFinishPolygon={store.finishPolygon}
          onCancelPolygon={store.cancelPolygon}
          polylineDrawing={store.polylineDrawing}
          onFinishPolyline={store.finishPolyline}
          onCancelPolyline={store.cancelPolyline}
        />
        <MarkupToolbar
          isMobileEnv={true}
          activeTool={store.activeTool}
          canUndo={store.canUndo}
          canRedo={store.canRedo}
          legendOpen={store.showLegend}
          showLegendHint={store.showLegendHint}
          onSelectTool={store.selectTool}
          onUndo={store.undo}
          onRedo={store.redo}
          onLegendOpenChange={store.setLegendOpen}
          showLocationTool={store.showLocationTool}
          isLocationEditActive={store.isLocationEditActive}
          onEditPreciseLocation={store.onEditPreciseLocation}
        />
      </div>
    )}
    {renderContextToolbar(store)}
    {renderTextToolbar(store)}
  </div>
))

function renderContextToolbar(store: MarkupEditorStore): JSX.Element {
  if (!store.selection || !store.selectionScreen || store.interacting)
    return null
  const pos = store.clampToolbarPos(
    store.selectionScreen.x,
    store.selectionScreen.y,
  )
  return (
    // Full-width strip pinned above the markup: the toolbar centers within it and
    // is width-capped to the viewport (scrolls if wider), so it's always entirely
    // on screen — mobile is too narrow to offset a near-full-width toolbar by its
    // anchor x without bleeding off an edge.
    <div
      className="tw:pointer-events-none tw:absolute tw:inset-x-2 tw:z-20 tw:flex tw:justify-center"
      style={{
        top: pos.y,
        transform: `translateY(calc(-100% - ${TOOLBAR_GAP}px))`,
      }}
    >
      <div
        ref={store.toolbarRef}
        className="tw:pointer-events-auto tw:max-w-full tw:overflow-x-auto"
      >
        <MarkupContextToolbar
          kind={store.selection.kind}
          color={store.selection.color}
          strokeWidth={store.selection.strokeWidth}
          strokeStyle={store.selection.strokeStyle}
          fillColor={store.selection.fillColor}
          fillStyle={store.selection.fillStyle}
          arrowheadStart={store.selection.arrowheadStart}
          arrowheadEnd={store.selection.arrowheadEnd}
          isMobileEnv={true}
          stylePreview={store.stylePreview}
          onColorChange={store.changeSelectedColor}
          onStrokeWidthChange={store.changeSelectedWidth}
          onStrokeStyleChange={store.changeSelectedStrokeStyle}
          onFillColorChange={store.changeSelectedFillColor}
          onFillStyleChange={store.changeSelectedFillStyle}
          onArrowheadChange={store.changeSelectedArrowhead}
          onDelete={store.deleteSelectedMarkup}
        />
      </div>
    </div>
  )
}

function renderTextToolbar(store: MarkupEditorStore): JSX.Element {
  const box = store.textStore.selected
  const screen = box && store.textScreens.get(box.id)
  if (!box || !screen) return null
  const base = store.textToolbarAnchor(box.id) ?? screen
  const anchor = store.clampToolbarPos(
    base.x,
    base.y,
    store.textToolbarRef.current,
  )
  return (
    // Full-width strip pinned above the box: the toolbar centers within it and is
    // width-capped to the viewport (scrolls if wider), so it's always entirely on
    // screen even when wider than the narrow mobile map. (See renderContextToolbar.)
    <div
      className="tw:pointer-events-none tw:absolute tw:inset-x-2 tw:z-20 tw:flex tw:justify-center"
      style={{
        top: anchor.y,
        transform: `translateY(calc(-100% - ${TOOLBAR_GAP}px))`,
      }}
    >
      <div
        ref={store.textToolbarRef}
        data-markup-text-toolbar
        onPointerDownCapture={() => store.markTextToolbarInteraction()}
        className="tw:pointer-events-auto tw:max-w-full tw:overflow-x-auto"
      >
        <MarkupTextContextToolbar
          color={box.color}
          fillColor={box.fillColor}
          fontSize={box.fontSize}
          bold={box.bold}
          italic={box.italic}
          underline={box.underline}
          isMobileEnv={true}
          stylePreview={store.stylePreview}
          onColorChange={store.changeTextColor}
          onFillColorChange={store.changeTextFillColor}
          onFontSizeChange={store.changeTextFontSize}
          onToggleBold={store.toggleTextBold}
          onToggleItalic={store.toggleTextItalic}
          onToggleUnderline={store.toggleTextUnderline}
          onDelete={store.deleteSelectedText}
        />
      </div>
    </div>
  )
}

export default MobileMarkupEditor
